import React, { useEffect } from "react";
import { ThemeProvider } from '@mui/material/styles';
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Header from "../Header/Header";
import Sidebar from "../Sidebar/Sidebar";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import {Footer} from "../Footer/Footer";
import {useMediaQuery} from "@mui/material";
import {useQueryUser} from "../../../queries/user/hooks/useQueryUser";
import NewPharmacy from "../NewPharmacy/NewPharmacy";
import PharmacyInfo from "../NewPharmacy/PharmacyInfo/PharmacyInfo";
import Routes from "../../Routes/Routes";
import {useQueryUniqueUsers} from "../../../queries/pharmacy/hooks/useQueryUniqueUsers";
import {ShoppingCartContextProvider} from "../../shared/ShoppingCart/ShoppingCartContextProvider";
import {contextObject} from "../../shared/ShoppingCart/ShoppingCartContext";
import {UserContext} from "../../../hooks/userContext";
import {PharmacyContext} from "../../../hooks/pharmacyContext";
import {mdTheme} from "../../../constants/mdTheme";
import {useNavigate} from "react-router-dom";
import BrandHeader from "../AuthLayout/BrandHeader";
import BrandFooter from "../AuthLayout/BrandFooter";
import BrandContent from "../AuthLayout/BrandContent";
import {brandAuthTheme} from "../../../constants/brandAuthTheme";
import BrandLoader from "../../shared/BrandLoader/BrandLoader";
import './appLayout.css';
import { routeConfigs } from "../../../navigation/routeConfig";

const AppLayout:React.FunctionComponent<any> = () => {
  const isMobileSize = useMediaQuery('(max-width:600px)');
  const [open, setOpen] = React.useState(!isMobileSize);

  const navigate = useNavigate();

  const {data, loading, error: userError} = useQueryUser();

  const {data: pharmacyUsersData, loading: pharmacyUsersLoading } = useQueryUniqueUsers({
    variables: { where: { id:  +data?.me?.id } },
    skip: !data
  });


  useEffect(() => {
    if (pharmacyUsersData?.findUniqueUser?.id && pharmacyUsersData.findUniqueUser.UserRoles[0]?.Role?.key !== 'admin') {
      const bankAccount = pharmacyUsersData.findUniqueUser.PharmacyUsers[0]?.Pharmacy?.BankAccount.find((item: any)=>(item.object === 'payment_method' || item.object === 'card') && (item.status === 'VERIFIED' || item.status === 'APPROVED'));
      if (!bankAccount) navigate(`${routeConfigs.bankAccount.path}/?required=true`)
    }
  }, [pharmacyUsersData?.findUniqueUser?.id]);


  if (loading || pharmacyUsersLoading) {
    return <BrandLoader fullscreen size={64} />;
  }

  const status = pharmacyUsersData?.findUniqueUser?.PharmacyUsers[0]?.Pharmacy?.PharmacyStatus?.Status?.key;
  const isAdmin = pharmacyUsersData?.findUniqueUser?.UserRoles[0]?.Role?.key === 'admin';

  if (!data?.me) {
    return <BrandLoader fullscreen size={64} />;
  }

  if ((status === 'PENDING' || status === 'REJECTED' || status === 'EXPIRED') && !isAdmin) {
    return (
      <ThemeProvider theme={brandAuthTheme}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
          <BrandHeader user={data?.me} pharmacy={pharmacyUsersData.findUniqueUser.PharmacyUsers[0].Pharmacy} />
          <BrandContent>
            <PharmacyInfo status={status} pharmacy={pharmacyUsersData?.findUniqueUser.PharmacyUsers[0]?.Pharmacy} />
          </BrandContent>
          <BrandFooter />
        </Box>
      </ThemeProvider>
    )
  }

  if (!status && !isAdmin) {
    return (
      <ThemeProvider theme={brandAuthTheme}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
          <BrandHeader user={data?.me} pharmacy={pharmacyUsersData?.findUniqueUser?.PharmacyUsers[0]?.Pharmacy} />
            <NewPharmacy user={data?.me} />
          <BrandFooter />
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <UserContext.Provider value={data.me}>
      <PharmacyContext.Provider value={pharmacyUsersData.findUniqueUser.PharmacyUsers[0]}>
        <ThemeProvider theme={mdTheme}>
          <Box sx={{display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            <ShoppingCartContextProvider<any> context={contextObject}>
              <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <CssBaseline />
                <Header
                  user={data?.me}
                  pharmacy={pharmacyUsersData?.findUniqueUser?.PharmacyUsers[0]?.Pharmacy}
                  setOpen={setOpen}
                  isAdmin={isAdmin}
                  open={open}
                />
                <Sidebar open={open} setOpen={setOpen} isAdmin={isAdmin} />
                <Box
                  component="main"
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.default,
                    flexGrow: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }}
                >
                  <Toolbar sx={{ flexShrink: 0, height: 64, minHeight: '64px !important' }} />
                  <Container
                    disableGutters
                    sx={{
                      pt: 1,
                      px: 1.5,
                      pb: 1,
                      width: '100%',
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      maxWidth: 'none !important',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'auto',
                      }}
                    >
                      <Routes isAdmin={isAdmin} />
                    </Box>
                  </Container>
                </Box>
              </Box>
            </ShoppingCartContextProvider>
            <Footer />
          </Box>
        </ThemeProvider>
      </PharmacyContext.Provider>
    </UserContext.Provider>
  )
}

export default AppLayout;